import { type GetListParams } from "react-admin";

import { createApiClient } from "@/api";
import { CLIENT_SEARCH_SOURCE } from "@/constants/filters";
import { type DataProviderSchema } from "@/utils";
import { checkAndThrowRequiredFieldsError } from "@/utils/errors";
import { getLocalFilteredSortedList } from "@/utils/listParams";

import { type ClientAppsFilter } from "./types";

const apiClient = createApiClient("json");
const apiClientLd = createApiClient("jsonld");
const BASE_URL = "/api/vpn/client_apps";

export const fetchClientAppsListData = async () => {
  const { data } = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: false,
      },
    },
  });
  return data;
};

const fetchListData = async (params: GetListParams) => {
  const typedFilter = params.filter as ClientAppsFilter;
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [CLIENT_SEARCH_SOURCE]: _,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    per_page: _perPage,
    ...restFilters
  } = typedFilter;
  const { data } = await apiClientLd.GET(BASE_URL, {
    params: {
      query: {
        ...(restFilters.pagination !== false && {
          page: params.pagination?.page,
        }),
        ...restFilters,
      },
    },
  });

  return {
    data: data?.["hydra:member"] ?? [],
    total: data?.["hydra:totalItems"] ?? 0,
  };
};

export const clientAppsSchema: DataProviderSchema<"ClientApp", "jsonld"> = {
  list: getLocalFilteredSortedList(fetchListData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: { path: { id: String(id) } },
    });
    if (!data) throw new Error("ClientApp not found");
    return {
      data: {
        ...data,
        allowedLandings: data.allowedLandings?.map(
          ({ id }) => `/api/vpn/landings/${id}`,
        ),
      },
    };
  },
  edit: async ({
    id,
    data: {
      title,
      email,
      isCampaignDriven,
      vpnServers,
      domain,
      allowedLandings,
      secret,
      diKey,
      deviceTypeId,
      copyVpnServers,
    },
    previousData: { secret: prevSecret, diKey: prevDiKey },
  }) => {
    const requiredFields = ["title", "email", "domain"];
    const body = checkAndThrowRequiredFieldsError(
      {
        title,
        email,
        isCampaignDriven,
        vpnServers,
        domain,
        allowedLandings,
        deviceTypeId,
        copyVpnServers,
        ...(prevSecret !== secret ? { secret } : {}),
        ...(prevDiKey !== diKey ? { diKey } : {}),
      },
      requiredFields,
    );
    const { data } = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: { path: { id: String(id) } },
      body,
    });
    if (!data) throw new Error("Something went wrong");
    return { data };
  },
  create: async ({
    data: {
      title,
      name,
      secret,
      email,
      deviceTypeId,
      isCampaignDriven,
      vpnServers,
      diKey,
      domain,
      allowedLandings,
      copyVpnServers,
    },
  }) => {
    const requiredFields = ["title", "name", "secret", "email", "domain"];
    const body = checkAndThrowRequiredFieldsError(
      {
        title,
        name,
        secret,
        email,
        deviceTypeId,
        isCampaignDriven,
        vpnServers,
        diKey,
        domain,
        allowedLandings,
        copyVpnServers,
      },
      requiredFields,
    );
    const { data } = await apiClient.POST(BASE_URL, {
      body,
    });
    if (!data) throw new Error("Something went wrong!");
    return { data };
  },
};
